// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, MessageSquare, BookOpen, Calendar, User, Search, Send } from 'lucide-react';
import ApiService from '../services/api';
import { apiFetch } from '../config/api';
import notificationService from '../utils/notificationService';
import usePiegeFocus from '../hooks/usePiegeFocus';
import './ProjectView.css';

const ProjectView = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddBookModal, setShowAddBookModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false); // FIX-009
  const [bookToRemove, setBookToRemove] = useState(null);
  const [availableBooks, setAvailableBooks] = useState([]);
  const [availableBooksError, setAvailableBooksError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooks, setSelectedBooks] = useState([]);

  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);

  // Dialogues : focus piégé, Échap ferme, focus rendu au bouton d'ouverture.
  const fermerAjout = () => setShowAddBookModal(false);
  const fermerRetrait = () => setShowRemoveModal(false);
  const fermerSuppression = () => setShowDeleteProjectModal(false);
  const refAjout = usePiegeFocus(showAddBookModal, fermerAjout);
  const refRetrait = usePiegeFocus(showRemoveModal && Boolean(bookToRemove), fermerRetrait);
  const refSuppression = usePiegeFocus(showDeleteProjectModal, fermerSuppression);

  const loadProject = useCallback(async () => {
    try {
      setLoading(true);
      const data = await ApiService.getProject(projectId);
      setProject(data.data || data);
    } catch (err) {
      setError(err?.message || 'Projet non trouvé');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const loadAvailableBooks = useCallback(async () => {
    try {
      setAvailableBooksError(null);
      const data = await ApiService.getBooks();
      const books = data.books || data || [];
      const projectBookIds = (project?.books || []).map(book => book.book_id || book.id);
      setAvailableBooks(books.filter(book => !projectBookIds.includes(book.book_id || book.id)));
    } catch (err) {
      // Une liste vide après un échec se confondait avec « aucun livre
      // disponible » : l'utilisateur ne pouvait pas savoir qu'il fallait
      // réessayer.
      setAvailableBooksError(err?.message || 'Chargement des livres impossible');
      setAvailableBooks([]);
    }
  }, [project]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  useEffect(() => {
    if (project) loadAvailableBooks();
  }, [project, loadAvailableBooks]);

  const basculerLivre = (bookId) => {
    setSelectedBooks((courants) =>
      courants.includes(bookId)
        ? courants.filter((id) => id !== bookId)
        : [...courants, bookId]
    );
  };

  const handleAddBooks = async () => {
    if (selectedBooks.length === 0) return;
    try {
      for (const bookId of selectedBooks) {
        await ApiService.addBookToProject(projectId, bookId);
      }
      const nombre = selectedBooks.length;
      setSelectedBooks([]);
      setShowAddBookModal(false);
      loadProject();
      notificationService.showToast(
        `${nombre} livre${nombre > 1 ? 's' : ''} ajouté${nombre > 1 ? 's' : ''} au projet`,
        'success'
      );
    } catch (err) {
      notificationService.showToast(err?.message || 'Erreur ajout des livres', 'error');
    }
  };

  const handleDeleteProject = () => setShowDeleteProjectModal(true);
  const confirmDeleteProject = async () => {
    try {
      await ApiService.deleteProject(projectId);
      navigate('/projects');
    } catch (err) {
      notificationService.showToast(err?.message || 'Erreur suppression', 'error');
    }
  };

  // Retirer un livre du projet
  const handleRemoveBook = async (bookId, bookTitle) => {
    setBookToRemove({ id: bookId, title: bookTitle });
    setShowRemoveModal(true);
  };

  const confirmRemoveBook = async () => {
    if (!bookToRemove) return;

    try {
      const response = await apiFetch(`projects/${projectId}/books/${bookToRemove.id}`, {
        method: 'DELETE',
      });
      // `apiFetch` rend la réponse telle quelle : sans ce contrôle, un 404 ou un
      // 500 fermait la fenêtre et annonçait un retrait qui n'avait pas eu lieu.
      if (!response.ok) {
        throw new Error(`Le serveur a refusé le retrait (HTTP ${response.status})`);
      }
      loadProject();
      setShowRemoveModal(false);
      setBookToRemove(null);
      notificationService.showToast('Livre retiré du projet', 'success');
    } catch (err) {
      notificationService.showToast(
        err?.message || 'Erreur lors du retrait du livre',
        'error'
      );
    }
  };

  // Envoyer un message à l'IA
  const sendMessage = async () => {
    if (!currentMessage.trim() || isLoadingResponse) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: currentMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsLoadingResponse(true);

    try {
      const formData = new FormData();
      formData.append('question', currentMessage);
      formData.append('project_id', projectId);
      formData.append('method', 'hybrid');
      formData.append('k', '5');
      formData.append('include_sources', 'true');

      const response = await apiFetch('ask', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        const aiMessage = {
          id: Date.now() + 1,
          type: 'ai',
          content: data.answer,
          sources: data.sources || [],
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, aiMessage]);
      } else {
        throw new Error('Erreur lors de la communication avec l\'IA');
      }
    } catch (err) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: 'Erreur: ' + err.message,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoadingResponse(false);
    }
  };

  // Filtrer les livres disponibles
  const filteredBooks = availableBooks.filter(book =>
    book.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.author?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="project-view-container app-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Chargement du projet...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-view-container app-page">
        <div className="error-message">
          <h3>Erreur</h3>
          <p>{error}</p>
          <button onClick={() => navigate('/projects')}>Retour aux projets</button>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-view-container app-page">
        <div className="error-message">
          <h3>Projet non trouvé</h3>
          <button onClick={() => navigate('/projects')}>Retour aux projets</button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-view-container app-page">
      {/* Header */}
      <div className="project-header">
        <div className="project-header-left">
          <button
            className="back-btn"
            onClick={() => navigate('/projects')}
          >
            <ArrowLeft size={20} />
            Retour
          </button>
          <div className="project-info">
            <h1>{project.name}</h1>
            {project.description && (
              <p className="project-description">{project.description}</p>
            )}
            <div className="project-meta">
              <span><Calendar size={16} /> {new Date(project.created_at * 1000).toLocaleDateString('fr-FR')}</span>
              <span><User size={16} /> {project.user_id}</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="add-books-btn" onClick={() => setShowAddBookModal(true)}>
            <Plus size={20} /> Ajouter des livres
          </button>
          <button className="action-btn delete-btn" onClick={handleDeleteProject} title="Supprimer le projet" style={{ padding: '8px 12px' }}>
            <Trash2 size={20} /> Supprimer le projet
          </button>
        </div>
      </div>

      <div className="project-content">
        {/* Section des livres */}
        <div className="books-section">
          <div className="section-header">
            <h2>
              <BookOpen size={24} />
              Livres du projet ({project.books.length})
            </h2>
          </div>

          {project.books.length === 0 ? (
            <div className="empty-books">
              <BookOpen size={48} />
              <h3>Aucun livre dans ce projet</h3>
              <p>Ajoutez des livres pour commencer à converser avec l'IA</p>
              <button
                className="add-books-btn"
                onClick={() => setShowAddBookModal(true)}
              >
                <Plus size={20} />
                Ajouter des livres
              </button>
            </div>
          ) : (
            <div className="books-grid">
              {project.books.map(book => (
                <div key={book.book_id} className="book-card">
                  <div className="book-info">
                    <h3>{book.title || 'Titre inconnu'}</h3>
                    {book.author && <p className="book-author">{book.author}</p>}
                    {book.year && <p className="book-year">{book.year}</p>}
                  </div>
                  <button
                    type="button"
                    className="remove-book-btn"
                    onClick={() => handleRemoveBook(book.book_id, book.title || 'Titre inconnu')}
                    title="Retirer du projet"
                    aria-label={`Retirer « ${book.title || 'Titre inconnu'} » du projet`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section chat IA */}
        <div className="chat-section">
          <div className="section-header">
            <h2>
              <MessageSquare size={24} />
              Chat IA - Contexte limité au projet
            </h2>
            <p className="chat-subtitle">
              Posez des questions sur les livres de ce projet uniquement
            </p>
          </div>

          <div className="chat-container">
            <div className="messages-container">
              {messages.length === 0 ? (
                <div className="empty-chat">
                  <MessageSquare size={48} />
                  <h3>Commencez la conversation</h3>
                  <p>Posez une question sur les livres de ce projet</p>
                </div>
              ) : (
                messages.map(message => (
                  <div key={message.id} className={`message ${message.type}`}>
                    <div className="message-content">
                      {message.content}
                    </div>
                    {message.sources && message.sources.length > 0 && (
                      <div className="message-sources">
                        <h4>Sources :</h4>
                        {message.sources.map((source, index) => (
                          <div key={index} className="source-item">
                            <strong>{source.book_title}</strong>
                            {source.book_author && ` - ${source.book_author}`}
                            <p>{source.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="message-timestamp">
                      {new Date(message.timestamp).toLocaleTimeString('fr-FR')}
                    </div>
                  </div>
                ))
              )}
              {isLoadingResponse && (
                <div className="message ai loading">
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="chat-input">
              <input
                type="text"
                aria-label="Question sur les livres du projet"
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Posez une question sur les livres de ce projet..."
                disabled={isLoadingResponse}
              />
              <button
                type="button"
                onClick={sendMessage}
                disabled={!currentMessage.trim() || isLoadingResponse}
                className="send-btn"
                aria-label="Envoyer la question"
                title="Envoyer la question"
              >
                <Send size={20} aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal d'ajout de livres */}
      {showAddBookModal && (
        // Clic sur le fond : raccourci souris ; au clavier, Échap ferme.
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) fermerAjout(); }}>
          <div
            className="modal-content"
            ref={refAjout}
            role="dialog"
            aria-modal="true"
            aria-labelledby="projet-ajout-titre"
            tabIndex={-1}
          >
            <div className="modal-header">
              <h2 id="projet-ajout-titre">Ajouter des livres au projet</h2>
              <button
                type="button"
                className="modal-close"
                onClick={fermerAjout}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="search-container">
                <Search size={20} aria-hidden />
                <input
                  type="text"
                  aria-label="Rechercher des livres"
                  placeholder="Rechercher des livres..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="books-list">
                {availableBooksError ? (
                  <div className="no-books" role="alert" data-testid="livres-erreur">
                    <p>Chargement impossible : {availableBooksError}</p>
                    <button type="button" onClick={loadAvailableBooks}>
                      Réessayer
                    </button>
                  </div>
                ) : filteredBooks.length === 0 ? (
                  <p className="no-books" data-testid="livres-vide">
                    {searchTerm.trim()
                      ? 'Aucun livre ne correspond à cette recherche'
                      : 'Tous vos livres sont déjà dans ce projet'}
                  </p>
                ) : (
                  // Une <label> autour d'une vraie case à cocher : la ligne entière
                  // reste cliquable, et la sélection se fait au clavier (Tab puis
                  // Espace). L'ancienne <div onClick> n'était pas focalisable, et
                  // sa case (onChange vide) ne réagissait qu'à la souris.
                  filteredBooks.map(book => (
                    <label
                      key={book.book_id}
                      className={`book-item ${selectedBooks.includes(book.book_id) ? 'selected' : ''}`}
                    >
                      <span className="book-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedBooks.includes(book.book_id)}
                          onChange={() => basculerLivre(book.book_id)}
                        />
                      </span>
                      <span className="book-details">
                        <h4>{book.title || 'Titre inconnu'}</h4>
                        {book.author && <p>{book.author}</p>}
                        {book.year && <span className="book-year">{book.year}</span>}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={fermerAjout}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleAddBooks}
                disabled={selectedBooks.length === 0}
              >
                Ajouter {selectedBooks.length} livre{selectedBooks.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression de livre */}
      {showRemoveModal && bookToRemove && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) fermerRetrait(); }}>
          <div
            className="modal-content"
            ref={refRetrait}
            role="dialog"
            aria-modal="true"
            aria-labelledby="projet-retrait-titre"
            tabIndex={-1}
          >
            <div className="modal-header">
              <h2 id="projet-retrait-titre">Confirmer la suppression</h2>
              <button
                type="button"
                className="modal-close"
                onClick={fermerRetrait}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>Êtes-vous sûr de vouloir retirer le livre <strong>"{bookToRemove.title}"</strong> de ce projet ?</p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowRemoveModal(false)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmRemoveBook}
              >
                Retirer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal suppression projet (FIX-009) */}
      {showDeleteProjectModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) fermerSuppression(); }}>
          <div
            className="modal-content"
            ref={refSuppression}
            role="dialog"
            aria-modal="true"
            aria-labelledby="projet-suppression-titre"
            tabIndex={-1}
          >
            <div className="modal-header">
              <h2 id="projet-suppression-titre">Supprimer le projet</h2>
              <button type="button" className="modal-close" onClick={fermerSuppression} aria-label="Fermer">×</button>
            </div>
            <div className="modal-body">
              <p>Êtes-vous sûr de vouloir supprimer ce projet ? Cette action est irréversible.</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowDeleteProjectModal(false)}>Annuler</button>
              <button type="button" className="btn-danger" onClick={confirmDeleteProject}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectView;
